import { useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as Popover from '@radix-ui/react-popover';
import { observer } from 'mobx-react-lite';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ChevronRight, ChevronDown, Menu, X, LogOut } from 'lucide-react';
import Logo from '@shared/assets/images/logos/logo.svg';
import IconImages from '@shared/assets/images/icons';
import { ROUTES } from '@shared/configs/constants';
import { useStore } from '@shared/hooks/useStore';
import { PACKAGE_TYPE, SYNCHRONOUS_ORIGIN } from '@shared/types/enums';
import { formatDateTime } from '@shared/utils/datetime';
import { DATE_YMD } from '@shared/components/constants/common.constants';

const navLinks = [
    { label: 'トップページ', href: ROUTES.home.href },
    { label: '書籍', href: ROUTES.books.href },
    { label: '雑誌', href: ROUTES.magazines.href },
    { label: '利用ガイド', href: ROUTES.usageGuide.href }
];

const Header = observer(() => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const [isUserMobileMenuOpen, setIsUserMobileMenuOpen] = useState(false);
    const navigate = useNavigate();

    const {
        modalStore: { showLoginModal },
        authStore: { isLoggedIn, isStdIndividual, isStdGroup, profile, logout }
    } = useStore();

    const handleSearch = () => {
        navigate(`${ROUTES.publications.href}?search=${encodeURIComponent(searchQuery.trim())}`);
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const [searchQuery, setSearchQuery] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get('search') || '';
    });

    const showLogin = () => {
        showLoginModal();
    };

    const closeUserMobileMenuCallback = () => {
        setIsUserMobileMenuOpen(false);
    };

    const closeMenuCallback = () => setIsMenuOpen(false);

    const renderPackageDate = () => {
        return `${formatDateTime(profile?.package?.startDate, DATE_YMD, { returnDefault: '不明' })} ~ ${!profile?.package?.hasExpiration ? '無制限' : formatDateTime(profile?.package?.expirationDate, DATE_YMD, { returnDefault: '不明' })}`;
    };

    const renderPackageRemain = () => {
        if (profile?.package?.packageType === PACKAGE_TYPE.UNLIMITED) {
            return '無制限';
        }
        if (!profile?.package?.maxSelectCount) {
            return '--';
        }
        return profile?.packageSelected?.remainSelected || 0;
    };

    const isTargetService = useMemo(() => {
        if (!isLoggedIn) return true;
        if (!profile?.synchronousOrigin) return false;
        return ![SYNCHRONOUS_ORIGIN.EE, SYNCHRONOUS_ORIGIN.EI].includes(profile.synchronousOrigin);
    }, [profile?.synchronousOrigin, isLoggedIn]);

    // ==================== DESKTOP COMPONENTS ====================

    const DesktopNav = () => (
        <nav className='hidden lg:flex items-center gap-8 xl:gap-12'>
            {navLinks.map(link => (
                <Link
                    key={link.href}
                    to={link.href}
                    className='leading-[26px] font-bold text-sm hover:text-primary transition-colors whitespace-nowrap'
                >
                    {link.label}
                </Link>
            ))}
        </nav>
    );

    const DesktopPackageInfo = () => {
        if (!isStdGroup || !profile?.package) return null;

        return (
            <div className='hidden lg:flex items-center gap-6 leading-[26px]'>
                <div className='flex flex-col items-center gap-1'>
                    <div className='flex items-center gap-2'>
                        <img src={IconImages.calendarIc} alt='' className='w-5 h-5' />
                        <span className='text-sm text-[#666666]'>利用期間</span>
                    </div>
                    <span className='text-sm font-bold'>{renderPackageDate()}</span>
                </div>
                <div className='flex flex-col items-center gap-1'>
                    <div className='flex items-center gap-2'>
                        <img src={IconImages.bookmarkIc} alt='' className='w-5 h-5' />
                        <span className='text-sm text-[#666666]'>残り読書可能冊数</span>
                    </div>
                    <span className='text-sm font-bold'>{renderPackageRemain()}</span>
                </div>
            </div>
        );
    };

    const DesktopUserMenu = () => (
        <Popover.Root open={isUserMenuOpen} onOpenChange={setIsUserMenuOpen}>
            <Popover.Trigger asChild>
                <button
                    className='hidden lg:flex items-center gap-2 outline-none'
                >
                    <img src={IconImages.avatarIc} alt='' className='w-10 h-10' />
                    <span className='hidden xl:inline text-sm font-bold leading-[26px]'>
                        {profile?.fullName && profile.fullName.length > 20 ? `${profile.fullName.slice(0, 20)}...` : profile?.fullName}
                    </span>
                    {isTargetService && (
                        <ChevronDown className={`w-5 h-5 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                    )}
                </button>
            </Popover.Trigger>
            {isTargetService && (
                <Popover.Portal>
                    <Popover.Content
                        align='end'
                        sideOffset={15}
                        className='z-50 bg-white border shadow-lg py-2 min-w-[160px] animate-in fade-in zoom-in-95 duration-200 outline-none'
                    >
                        <Link
                            to={ROUTES.profile.href}
                            className='block px-4 py-2 text-sm hover:bg-gray-50'
                            onClick={() => setIsUserMenuOpen(false)}
                        >
                            マイページ
                        </Link>
                        <hr className='my-2' />
                        <button
                            className='w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-50'
                            onClick={() => {
                                logout();
                                setIsUserMenuOpen(false);
                            }}
                        >
                            ログアウト
                        </button>
                    </Popover.Content>
                </Popover.Portal>
            )}
        </Popover.Root>
    );

    const DesktopCartBookshelf = () => (
        <div className='hidden absolute top-1/2 right-0 -translate-y-1/2 lg:flex items-center gap-6'>
            {isStdIndividual && (
                <Link to={ROUTES.cart.href} className='flex flex-col items-center hover:text-primary'>
                    <img src={IconImages.cartIc} alt='' className='w-6 h-6' />
                    <span className='text-xs'>カート</span>
                </Link>
            )}
            <Link to={ROUTES.myLibraryPublications.href} className='flex flex-col items-center hover:text-primary'>
                <img src={IconImages.libraryIc} alt='' className='w-6 h-6' />
                <span className='text-xs'>マイ本棚</span>
            </Link>
        </div>
    );

    const DesktopAuthButtons = () => (
        <div className='hidden lg:flex items-center gap-3'>
            <button
                onClick={showLogin}
                className='flex items-center justify-center gap-1 bg-[#09AA6E] text-white w-[176px] h-14 rounded-lg font-semibold text-sm hover:bg-[#088a5a] transition-colors'
            >
                ログイン
                <ChevronRight className='w-5 h-5' />
            </button>
            <Link
                to='/registration/first-step?sub=1'
                className='flex items-center justify-center bg-[#016743] text-white w-[176px] h-14 rounded-lg font-semibold text-sm hover:bg-[#015535] transition-colors'
            >
                新規登録
            </Link>
        </div>
    );

    // ==================== MOBILE COMPONENTS ====================

    const MobileHeaderIcons = () => {
        if (!isLoggedIn) return null;

        return (
            <div className='flex lg:hidden items-center gap-3'>
                {isStdIndividual && (
                    <Link to={ROUTES.cart.href}>
                        <img src={IconImages.cartIc} alt='' className='w-6 h-6' />
                    </Link>
                )}
                <MobileUserMenu
                    isOpen={isUserMobileMenuOpen}
                    onOpenChange={setIsUserMobileMenuOpen}
                    profile={profile}
                    isStdGroup={isStdGroup}
                    isStdIndividual={isStdIndividual}
                    renderPackageDate={renderPackageDate}
                    renderPackageRemain={renderPackageRemain}
                    logout={logout}
                    closeMenu={closeUserMobileMenuCallback}
                    isTargetService={isTargetService}
                />
            </div>
        );
    };
    // ==================== MAIN RENDER ====================

    return (
        <header className='sticky top-0 z-30 bg-white border-b'>
            <div className='container mx-auto px-4 py-3'>
                <div className='relative flex items-center justify-between h-10'>
                    <div className='flex items-center gap-5 lg:gap-8'>
                        {isTargetService && (
                            <button
                                onClick={() => setIsMenuOpen(!isMenuOpen)}
                                className='lg:hidden p-1 -ml-1 outline-none'
                                aria-label='メニュー'
                            >
                                <Menu className='w-6 h-6' />
                            </button>
                        )}

                        {
                            isTargetService ?
                                (
                                    <Link to='/' className='hidden lg:block shrink-0'>
                                        <img src={Logo} alt='じほう電子コンテンツストア' className='h-12' />
                                    </Link>
                                ) :
                                (
                                    <img src={Logo} alt='じほう電子コンテンツストア' className='hidden lg:block shrink-0 h-12' />
                                )
                        }

                        {isTargetService && <DesktopNav />}
                    </div>

                    {
                        isTargetService ?
                            (
                                <Link to='/' className='lg:hidden absolute left-1/2 -translate-x-1/2'>
                                    <img src={Logo} alt='じほう電子コンテンツストア' className='h-10' />
                                </Link>
                            ) :
                            (
                                <img src={Logo} alt='じほう電子コンテンツストア' className='lg:hidden absolute left-1/2 -translate-x-1/2 h-10' />
                            )
                    }

                    <div className='flex items-center gap-4 lg:gap-6'>
                        {isLoggedIn ?
                            (
                                <>
                                    <DesktopPackageInfo />
                                    <DesktopUserMenu />
                                    <MobileHeaderIcons />
                                </>
                            ) :
                            (
                                <DesktopAuthButtons />
                            )}
                    </div>
                </div>

                <div className='mt-4 flex items-center relative'>
                    <div className='w-full flex justify-center'>
                        <div className='w-full max-w-[573px] relative h-12'>
                            {isTargetService && (
                                <>
                                    <button
                                        onClick={handleSearch}
                                        className='absolute left-3 top-1/2 -translate-y-1/2'
                                    >
                                        <Search className='w-5 h-5 text-gray-500' />
                                    </button>
                                    <input
                                        type='text'
                                        placeholder='書籍名・雑誌名で検索'
                                        value={searchQuery}
                                        onChange={e => setSearchQuery(e.target.value)}
                                        onKeyDown={handleSearchKeyDown}
                                        className='w-full h-full pl-10 pr-4 border border-black rounded focus:outline-none focus:border-primary'
                                    />
                                </>
                            )}
                        </div>
                    </div>

                    {isLoggedIn && (
                        <div className='hidden lg:block'>
                            <DesktopCartBookshelf />
                        </div>
                    )}
                </div>
            </div>

            <MobileMenu
                isOpen={isMenuOpen}
                onOpenChange={setIsMenuOpen}
                closeMenu={closeMenuCallback}
                isLoggedIn={isLoggedIn}
                showLogin={showLogin}
            />
        </header>
    );
});

// ==================== SUB-COMPONENTS (Outside Header to prevent re-creation) ====================

const MobileUserMenu = ({
    isOpen,
    onOpenChange,
    profile,
    isStdGroup,
    isStdIndividual,
    renderPackageDate,
    renderPackageRemain,
    logout,
    closeMenu,
    isTargetService
}: any) => {
    const [isMyLibraryOpen, setIsMyLibraryOpen] = useState(false);

    return (
        <Popover.Root open={isOpen} onOpenChange={onOpenChange}>
            <Popover.Trigger asChild>
                <button className='outline-none'>
                    <img src={IconImages.avatarIc} alt='' className='w-8 h-8' />
                </button>
            </Popover.Trigger>
            <Popover.Portal>
                <Popover.Content
                    align='end'
                    sideOffset={12}
                    className='z-50 w-[334px] bg-white shadow-[0px_0px_4px_0px_#00000040] animate-in fade-in zoom-in-95 duration-200 outline-none overflow-hidden mr-4'
                >
                    <div className='px-5 py-4'>
                        {isTargetService ?
                            (
                                <Link to={ROUTES.profile.href} className='flex items-center gap-3 mb-6' onClick={closeMenu}>
                                    <img src={IconImages.avatarIc} alt='' className='w-12 h-12' />
                                    <span className='text-lg font-bold line-clamp-1'>
                                        {profile?.fullName}
                                    </span>
                                </Link>
                            ) :
                            (
                                <div className='flex items-center gap-3 mb-6'>
                                    <img src={IconImages.avatarIc} alt='' className='w-12 h-12' />
                                    <span className='text-lg font-bold line-clamp-1'>
                                        {profile?.fullName}
                                    </span>
                                </div>
                            )}

                        {isStdGroup && profile?.package && (
                            <div className='space-y-3 mb-6 leading-[26px]'>
                                <div className='flex items-center gap-3'>
                                    <img src={IconImages.calendarIc} alt='' className='w-5 h-5' />
                                    <span className='text-sm text-[#666666]'>利用期間</span>
                                    <span className='text-sm font-bold'>
                                        {renderPackageDate()}
                                    </span>
                                </div>
                                <div className='flex items-center gap-3'>
                                    <img src={IconImages.bookmarkIc} alt='' className='w-5 h-5' />
                                    <span className='text-sm text-[#666666]'>残り読書可能冊数</span>
                                    <span className='text-sm font-bold'>{renderPackageRemain()}</span>
                                </div>
                            </div>
                        )}

                        {isTargetService && (
                            <hr className='my-4' />
                        )}

                        <nav className='space-y-1'>

                            {isStdIndividual && (
                                <>
                                    <Link
                                        to={ROUTES.purchaseHistory.href}
                                        className='flex items-center gap-3 py-3 text-sm'
                                        onClick={closeMenu}
                                    >
                                        <img src={IconImages.creditCardIc} alt='' className='w-5 h-5' />
                                        購入履歴
                                    </Link>
                                    <Link
                                        to={ROUTES.cart.href}
                                        className='flex items-center gap-3 py-3 text-sm'
                                        onClick={closeMenu}
                                    >
                                        <img src={IconImages.cartIc} alt='' className='w-5 h-5' />
                                        カート
                                    </Link>
                                </>
                            )}

                            <div>
                                <button
                                    onClick={() => setIsMyLibraryOpen(!isMyLibraryOpen)}
                                    className='flex items-center justify-between w-full py-3 text-sm'
                                >
                                    <div className='flex items-center gap-3'>
                                        <img src={IconImages.libraryIc} alt='' className='w-5 h-5' />
                                        マイ本棚
                                    </div>
                                    <ChevronDown
                                        className={`w-5 h-5 transition-transform ${isMyLibraryOpen ? 'rotate-180' : ''}`}
                                    />
                                </button>

                                {isMyLibraryOpen && (
                                    <div className='pl-8 space-y-1'>
                                        <Link
                                            to={ROUTES.myLibraryPublications.href}
                                            className='block py-3 text-sm'
                                            onClick={closeMenu}
                                        >
                                            {isStdIndividual ? '購入済み書籍・雑誌' : '指定書籍・雑誌'}
                                        </Link>
                                        <Link
                                            to={ROUTES.myLibraryReadingHistory.href}
                                            className='block py-3 text-sm'
                                            onClick={closeMenu}
                                        >
                                            読書履歴
                                        </Link>
                                        <Link
                                            to={ROUTES.myLibraryFavorites.href}
                                            className='block py-3 text-sm'
                                            onClick={closeMenu}
                                        >
                                            お気に入り
                                        </Link>
                                    </div>
                                )}
                            </div>
                        </nav>

                        {isTargetService && (
                            <>
                                <hr className='my-4' />
                                <button
                                    onClick={() => {
                                        logout();
                                        closeMenu();
                                    }}
                                    className='flex items-center gap-3 py-3 text-sm text-danger'
                                >
                                    <LogOut className='w-5 h-5' />
                                    ログアウト
                                </button>
                            </>
                        )}
                    </div>
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
};

const MobileMenu = ({ isOpen, onOpenChange, closeMenu, isLoggedIn, showLogin }: any) => {
    return (
        <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
            <Dialog.Portal>
                <Dialog.Overlay className='fixed inset-0 z-50 bg-[#00000026] backdrop-blur-[5.8px] animate-in fade-in duration-300' />
                <Dialog.Content className='fixed inset-x-0 top-0 z-50 bg-white w-full animate-in slide-in-from-top duration-300 focus:outline-none'>
                    <div className='flex items-center justify-between px-4 py-3 border-b'>
                        <button onClick={closeMenu} className='p-1 outline-none'>
                            <X className='w-6 h-6' />
                        </button>
                        <img src={Logo} alt='じほう電子コンテンツストア' className='h-10' />
                        <div className='w-8' />
                    </div>

                    <div className='overflow-y-auto px-6 py-8'>
                        <nav className='space-y-[48px]'>
                            {navLinks.map(link => (
                                <Link
                                    key={link.href}
                                    to={link.href}
                                    className='block text-sm leading-[26px] font-bold'
                                    onClick={closeMenu}
                                >
                                    {link.label}
                                </Link>
                            ))}
                        </nav>

                        {!isLoggedIn && (
                            <div className='flex gap-3 mt-6'>
                                <button
                                    className='flex-1 flex items-center justify-center h-[56px] gap-1 bg-[#09AA6E] text-white rounded-lg text-sm font-semibold'
                                    onClick={() => {
                                        closeMenu();
                                        showLogin();
                                    }}
                                >
                                    ログイン
                                    <ChevronRight className='w-5 h-5' />
                                </button>
                                <Link
                                    to='/registration/first-step?sub=1'
                                    className='flex-1 flex items-center justify-center h-[56px] bg-[#016743] text-white rounded-lg text-sm font-semibold'
                                    onClick={closeMenu}
                                >
                                    新規登録
                                </Link>
                            </div>
                        )}
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

export default Header;
